/**
 * UI State Management Store (Zustand)
 * Manages UI state, selection, panels, theme, etc.
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import {
  UIState,
  Theme,
  ContextMenuState,
  ClipboardData,
  SelectedHandle,
} from '@flow-studio/types';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
}

export interface UIStoreState extends UIState {
  // Toast notifications
  toasts: Toast[];

  // Actions
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;

  setSelectedNodes: (nodeIds: string[]) => void;
  addSelectedNode: (nodeId: string) => void;
  removeSelectedNode: (nodeId: string) => void;
  clearSelection: () => void;

  setSelectedEdges: (edgeIds: string[]) => void;
  addSelectedEdge: (edgeId: string) => void;
  removeSelectedEdge: (edgeId: string) => void;

  setSelectedHandle: (handle: SelectedHandle | null) => void;

  setLeftPanelOpen: (open: boolean) => void;
  setRightPanelOpen: (open: boolean) => void;
  setBottomPanelOpen: (open: boolean) => void;
  setObserveActive: (active: boolean) => void;
  toggleLeftPanel: () => void;
  toggleRightPanel: () => void;
  toggleBottomPanel: () => void;

  setContextMenu: (menu: ContextMenuState | null) => void;
  closeContextMenu: () => void;

  setClipboard: (data: ClipboardData | null) => void;

  setViewport: (viewport: { x: number; y: number; zoom: number }) => void;

  setSnapToGrid: (snap: boolean) => void;
  setGridSize: (size: number) => void;
  setShowGrid: (show: boolean) => void;
  setShowMinimap: (show: boolean) => void;
  setUseSmartEdges: (use: boolean) => void;
  toggleSmartEdges: () => void;
  setHideSatellites: (v: boolean) => void;
  toggleHideSatellites: () => void;
  setHideUnassignedPorts: (v: boolean) => void;
  toggleHideUnassignedPorts: () => void;
  setAllNodesCollapsed: (v: boolean) => void;
  setHilFormsVisible: (v: boolean) => void;
  toggleHilFormsVisible: () => void;
  setHilChatVisible: (v: boolean) => void;
  toggleHilChatVisible: () => void;

  // Per-node hidden satellites
  hideNodes: (ids: string[]) => void;
  showNodes: (ids: string[]) => void;
  showAllNodes: () => void;

  // Per-node hide unused ports
  toggleNodePortHide: (nodeId: string) => void;
  clearNodePortHides: () => void;

  // Node resize feature toggle
  toggleNodeResizeEnabled: () => void;

  // Canvas lock
  toggleCanvasLock: () => void;

  // Auto-save toggle
  toggleAutoSave: () => void;

  // Component visibility
  toggleHideComponentsGlobally: () => void;
  toggleComponentHideForNode: (parentNodeId: string) => void;

  setIsDragging: (dragging: boolean) => void;
  setIsConnecting: (connecting: boolean) => void;
  setIsPanning: (panning: boolean) => void;

  // Toast actions
  showToast: (type: ToastType, message: string, duration?: number) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;

  // Modal actions
  openModal: (modalId: string, data?: any) => void;

  // Pending delete (keyboard shortcut confirmation)
  pendingDeleteNodeIds: string[];
  pendingDeleteEdgeIds: string[];
  requestDeleteSelected: (nodeIds: string[], edgeIds: string[]) => void;
  clearPendingDelete: () => void;
}

export const useUIStore = create<UIStoreState>()(
  devtools(
    persist(
      (set) => ({
        // Initial State
        theme: 'dark',
        selectedNodes: [],
        selectedEdges: [],
        selectedGroups: [],
        selectedHandle: null,

        leftPanelOpen: true,
        rightPanelOpen: true,
        bottomPanelOpen: false,
        observeActive: false,

        contextMenu: null,
        clipboard: null,

        viewport: {
          x: 0,
          y: 0,
          zoom: 1,
        },

        snapToGrid: false,
        gridSize: 20,
        showGrid: true,
        showMinimap: false,
        useSmartEdges: false,
        hideSatellites: false,
        hideUnassignedPorts: false,
        allNodesCollapsed: false,
        hiddenNodeIds: [],
        hiddenPortsNodeIds: [],
        nodeResizeEnabled: false,
        isCanvasLocked: false,
        autoSaveEnabled: true,
        hideComponentsGlobally: true,
        hiddenComponentParentIds: [],
        // HIL surface visibility — both on by default (View menu can still hide either). Chat
        // used to default off; that combined with a separate envelope-normalization bug (see
        // useExecutionSignalR.ts's ReceiveHilEvent fallback path) made the chat window look
        // completely non-functional rather than just hidden. Defaulting it on matches Forms and
        // avoids a working chat session being invisible until someone happens to find the toggle.
        hilFormsVisible: true,
        hilChatVisible: true,

        isDragging: false,
        isConnecting: false,
        isPanning: false,

        toasts: [],

        pendingDeleteNodeIds: [],
        pendingDeleteEdgeIds: [],

        // Theme Actions
        setTheme: (theme: Theme) => set({ theme }),
        toggleTheme: () =>
          set((state) => ({
            theme: state.theme === 'light' ? 'dark' : 'light',
          })),

        // Selection Actions
        setSelectedNodes: (nodeIds: string[]) =>
          set({ selectedNodes: nodeIds, selectedHandle: null }),
        addSelectedNode: (nodeId: string) =>
          set((state) => ({
            selectedNodes: [...state.selectedNodes, nodeId],
          })),
        removeSelectedNode: (nodeId: string) =>
          set((state) => ({
            selectedNodes: state.selectedNodes.filter((id) => id !== nodeId),
          })),
        clearSelection: () =>
          set({ selectedNodes: [], selectedEdges: [], selectedGroups: [], selectedHandle: null }),

        setSelectedEdges: (edgeIds: string[]) =>
          set({ selectedEdges: edgeIds, selectedHandle: null }),
        addSelectedEdge: (edgeId: string) =>
          set((state) => ({
            selectedEdges: [...state.selectedEdges, edgeId],
          })),
        removeSelectedEdge: (edgeId: string) =>
          set((state) => ({
            selectedEdges: state.selectedEdges.filter((id) => id !== edgeId),
          })),

        setSelectedHandle: (handle: SelectedHandle | null) =>
          set({ selectedHandle: handle }),

        // Panel Actions
        setLeftPanelOpen: (open: boolean) => set({ leftPanelOpen: open }),
        setRightPanelOpen: (open: boolean) => set({ rightPanelOpen: open }),
        setBottomPanelOpen: (open: boolean) => set({ bottomPanelOpen: open }),
        setObserveActive: (active: boolean) => set({ observeActive: active }),
        toggleLeftPanel: () =>
          set((state) => ({ leftPanelOpen: !state.leftPanelOpen })),
        toggleRightPanel: () =>
          set((state) => ({ rightPanelOpen: !state.rightPanelOpen })),
        toggleBottomPanel: () =>
          set((state) => ({ bottomPanelOpen: !state.bottomPanelOpen })),

        // Context Menu Actions
        setContextMenu: (menu: ContextMenuState | null) =>
          set({ contextMenu: menu }),
        closeContextMenu: () => set({ contextMenu: null }),

        // Clipboard Actions
        setClipboard: (data: ClipboardData | null) => set({ clipboard: data }),

        // Viewport Actions
        setViewport: (viewport: { x: number; y: number; zoom: number }) =>
          set({ viewport }),

        // Settings Actions
        setSnapToGrid: (snap: boolean) => set({ snapToGrid: snap }),
        setGridSize: (size: number) => set({ gridSize: size }),
        setShowGrid: (show: boolean) => set({ showGrid: show }),
        setShowMinimap: (show: boolean) => set({ showMinimap: show }),
        setUseSmartEdges: (use: boolean) => set({ useSmartEdges: use }),
        toggleSmartEdges: () =>
          set((state) => ({ useSmartEdges: !state.useSmartEdges })),
        setHideSatellites: (v: boolean) => set({ hideSatellites: v }),
        toggleHideSatellites: () =>
          set((state) => ({ hideSatellites: !state.hideSatellites })),
        setHideUnassignedPorts: (v: boolean) => set({ hideUnassignedPorts: v }),
        toggleHideUnassignedPorts: () =>
          set((state) => ({ hideUnassignedPorts: !state.hideUnassignedPorts })),
        setAllNodesCollapsed: (v: boolean) => set({ allNodesCollapsed: v }),
        setHilFormsVisible: (v: boolean) => set({ hilFormsVisible: v }),
        toggleHilFormsVisible: () =>
          set((state) => ({ hilFormsVisible: !state.hilFormsVisible })),
        setHilChatVisible: (v: boolean) => set({ hilChatVisible: v }),
        toggleHilChatVisible: () =>
          set((state) => ({ hilChatVisible: !state.hilChatVisible })),

        // Per-node hidden satellites
        hideNodes: (ids: string[]) =>
          set((state) => ({
            hiddenNodeIds: [...new Set([...state.hiddenNodeIds, ...ids])],
          })),
        showNodes: (ids: string[]) =>
          set((state) => ({
            hiddenNodeIds: state.hiddenNodeIds.filter((id) => !ids.includes(id)),
          })),
        showAllNodes: () => set({ hiddenNodeIds: [] }),

        // Per-node hide unused ports
        toggleNodePortHide: (nodeId: string) =>
          set((state) => ({
            hiddenPortsNodeIds: state.hiddenPortsNodeIds.includes(nodeId)
              ? state.hiddenPortsNodeIds.filter(id => id !== nodeId)
              : [...state.hiddenPortsNodeIds, nodeId],
          })),
        clearNodePortHides: () => set({ hiddenPortsNodeIds: [] }),

        toggleNodeResizeEnabled: () =>
          set((state) => ({ nodeResizeEnabled: !state.nodeResizeEnabled })),

        toggleCanvasLock: () =>
          set((state) => ({ isCanvasLocked: !state.isCanvasLocked })),

        toggleAutoSave: () =>
          set((state) => ({ autoSaveEnabled: !state.autoSaveEnabled })),

        toggleHideComponentsGlobally: () =>
          set((state) => ({ hideComponentsGlobally: !state.hideComponentsGlobally })),

        toggleComponentHideForNode: (parentNodeId: string) =>
          set((state) => ({
            hiddenComponentParentIds: state.hiddenComponentParentIds.includes(parentNodeId)
              ? state.hiddenComponentParentIds.filter(id => id !== parentNodeId)
              : [...state.hiddenComponentParentIds, parentNodeId],
          })),

        // Interaction State Actions
        setIsDragging: (dragging: boolean) => set({ isDragging: dragging }),
        setIsConnecting: (connecting: boolean) =>
          set({ isConnecting: connecting }),
        setIsPanning: (panning: boolean) => set({ isPanning: panning }),

        // Toast Actions
        showToast: (type: ToastType, message: string, duration: number = 3000) => {
          const id = `toast-${Date.now()}-${Math.random()}`;
          const toast: Toast = { id, type, message, duration };

          set((state) => ({
            toasts: [...state.toasts, toast],
          }));

          // Auto-remove toast after duration
          if (duration > 0) {
            setTimeout(() => {
              set((state) => ({
                toasts: state.toasts.filter((t) => t.id !== id),
              }));
            }, duration);
          }
        },
        removeToast: (id: string) =>
          set((state) => ({
            toasts: state.toasts.filter((t) => t.id !== id),
          })),
        clearToasts: () => set({ toasts: [] }),

        // Modal Actions
        openModal: (_modalId: string, _data?: any) => {
          console.warn('[uiStore] openModal not yet implemented');
        },

        // Pending delete actions
        requestDeleteSelected: (nodeIds: string[], edgeIds: string[]) =>
          set({ pendingDeleteNodeIds: nodeIds, pendingDeleteEdgeIds: edgeIds }),
        clearPendingDelete: () =>
          set({ pendingDeleteNodeIds: [], pendingDeleteEdgeIds: [] }),
      }),
      {
        name: 'ui-store',
        partialize: (state) => ({
          theme: state.theme,
          rightPanelOpen: state.rightPanelOpen,
          bottomPanelOpen: state.bottomPanelOpen,
          observeActive: state.observeActive,
          snapToGrid: state.snapToGrid,
          gridSize: state.gridSize,
          showGrid: state.showGrid,
          showMinimap: state.showMinimap,
          useSmartEdges: state.useSmartEdges,
          hideSatellites: state.hideSatellites,
          hideUnassignedPorts: state.hideUnassignedPorts,
          allNodesCollapsed: state.allNodesCollapsed,
          nodeResizeEnabled: state.nodeResizeEnabled,
          isCanvasLocked: state.isCanvasLocked,
          autoSaveEnabled: state.autoSaveEnabled,
          hideComponentsGlobally: state.hideComponentsGlobally,
          hiddenComponentParentIds: state.hiddenComponentParentIds,
          viewport: state.viewport,
          hilFormsVisible: state.hilFormsVisible,
          hilChatVisible: state.hilChatVisible,
        }),
      }
    ),
    { name: 'UIStore' }
  )
);
